import { Task } from "gantt-task-react";
import {
  Droppable,
  Draggable,
  DragDropContext,
  DropResult,
} from "@hello-pangea/dnd";
import React from "react";
import { Button } from "primereact/button";
import { Workpack } from "../helper";

interface TaskListTableProps {
  rowHeight: number;
  rowWidth: string;
  fontFamily: string;
  fontSize: string;
  tasks: any[];
  workpack: Workpack | null;
  onExpanderClick: (task: Task) => void;
  handleAddTask: (task: Task) => void;
  setDate: (task: Task) => void;
  onReorder?: (sourceIndex: number, destIndex: number) => void;
  onJCLockClick: (jcId: number, isLocked: boolean) => void;
}

// Fixed column widths for alignment with header
const NAME_COLUMN_WIDTH = 300;
const DATE_COLUMN_WIDTH = 150;
const HRS_COLUMN_WIDTH = 120;
const LOCK_COLUMN_WIDTH = 20;

const CustomTaskListTable: React.FC<TaskListTableProps> = ({
  tasks,
  rowHeight,
  fontFamily,
  fontSize,
  workpack,
  onExpanderClick,
  setDate,
  onReorder,
  onJCLockClick,
}) => {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || !onReorder) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const sourceTask = tasks[sourceIndex];
    const destTask = tasks[destIndex];

    // Prevent dragging across different Job Cards or moving Job Cards themselves
    if (
      sourceTask.project !== destTask.project ||
      sourceTask.type === "project" ||
      destTask.type === "project"
    ) {
      console.warn("Tasks can only be reordered within the same Job Card.");
      return;
    }

    // Call parent reorder handler
    onReorder(sourceIndex, destIndex);
  };

  const formatDate = (date: any) => {
    if (!date) return "—";
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return "—";
    }
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="taskList">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="task-list-table"
            >
              {tasks.map((task, i) => {
                const isProject = task.type === "project";

                // Render Job Card rows (non-draggable)
                if (isProject) {
                  const childTasks = tasks.filter((t) => t.project === task.id);
                  const isLocked =
                    childTasks.length > 0 &&
                    childTasks.every((t) => t.is_locked);
                  return (
                    <div
                      key={task.id}
                      className="task-list-item"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: rowHeight,
                        fontFamily,
                        fontSize,
                        backgroundColor: "#8CCDEB",
                        borderBottom: "1px solid #ccc",
                        fontWeight: 600,
                      }}
                    >
                      {/* Spacer for handle alignment */}
                      <div style={{ width: 24, padding: "0 8px" }} />

                      {/* Name Column */}
                      <div
                        style={{
                          width: NAME_COLUMN_WIDTH,
                          minWidth: NAME_COLUMN_WIDTH,
                          maxWidth: NAME_COLUMN_WIDTH,
                          paddingLeft: 5,
                          paddingRight: 10,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          lineHeight: "1.4em",
                        }}
                      >
                        <p className="m-0">{task.op_no}</p>
                        <p
                          className="m-0"
                          title={task.name}
                          onClick={() => onExpanderClick(task)}
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            lineHeight: "1.3em",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          {task.hideChildren ? "► " : "▼ "} {task.name}{" "}
                        </p>
                      </div>

                      {/* Start Date */}
                      <div
                        style={{
                          width: DATE_COLUMN_WIDTH,
                          textAlign: "center",
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          lineHeight: "1.3em",
                        }}
                      >
                        <span style={{ fontWeight: 600 }}>
                          {formatDate(task.actual_start)}
                        </span>
                        <small style={{ color: "#555" }}>
                          ({formatDate(task.start)})
                        </small>
                      </div>

                      {/* End Date */}
                      <div
                        style={{
                          width: DATE_COLUMN_WIDTH,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          textAlign: "center",
                          height: "100%",
                          lineHeight: "1.3em",
                        }}
                      >
                        <span style={{ fontWeight: 600, color: "#000" }}>
                          {formatDate(task.actual_end)}
                        </span>
                        <small style={{ color: "#777", fontStyle: "italic" }}>
                          ({formatDate(task.end)})
                        </small>
                      </div>

                      {/* Budgeted Hrs */}
                      <div
                        style={{
                          width: HRS_COLUMN_WIDTH,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          height: "100%",
                          lineHeight: "1.3em",
                        }}
                      >
                        {task.durations}
                      </div>

                      {/* Lock column for Job Card (JC) */}
                      <div
                        style={{
                          width: LOCK_COLUMN_WIDTH,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <i
                          className={`pi ${
                            isLocked ? "pi-lock" : "pi-lock-open"
                          }`}
                          style={{
                            color: isLocked ? "#EF4444" : "#22C55E",
                            fontSize: "1.4rem",
                            cursor: "pointer",
                            marginLeft: "-30px",
                          }}
                          title="Lock/unlock all tasks of this Job Card"
                          onClick={() => onJCLockClick(task.id, isLocked)}
                        />
                      </div>
                    </div>
                  );
                }

                // Render Child Tasks (draggable)
                return (
                  <Draggable
                    key={task.id}
                    draggableId={task.id.toString()}
                    index={i}
                  >
                    {(providedDrag, snapshot) => (
                      <div
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        className="task-list-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          minHeight: rowHeight,
                          fontFamily,
                          fontSize,
                          backgroundColor: snapshot.isDragging
                            ? "#E3F2FD"
                            : "#F8F7BA",
                          borderBottom: "2px solid #ccc",
                          padding: "4px 0",
                          ...providedDrag.draggableProps.style,
                        }}
                      >
                        {/* Drag Handle */}
                        <div
                          {...providedDrag.dragHandleProps}
                          style={{
                            cursor: "grab",
                            padding: "0 8px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <i
                            className="pi pi-bars"
                            style={{ color: "#6c757d", fontSize: "1rem" }}
                          />
                        </div>

                        {/* Name Column */}
                        <div
                          style={{
                            width: NAME_COLUMN_WIDTH,
                            minWidth: NAME_COLUMN_WIDTH,
                            maxWidth: NAME_COLUMN_WIDTH,
                            paddingLeft: 5,
                            paddingRight: 10,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                          }}
                        >
                          <p className="m-0">{task.op_no}</p>
                          <div
                            className="m-0"
                            title={task.name}
                            onClick={() => onExpanderClick(task)}
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              lineHeight: "1.3em",
                              textAlign: "left",
                            }}
                          >
                            <span>{task.name}</span>
                          </div>
                        </div>

                        {/* Start / End Dates */}
                        {task.taskdateFlag ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: DATE_COLUMN_WIDTH * 2,
                            }}
                          >
                            <Button
                              severity="danger"
                              disabled={
                                workpack?.construction &&
                                workpack?.offshore &&
                                workpack?.onshore
                              }
                              onClick={() => setDate(task)}
                              icon="pi pi-plus"
                              size="small"
                            />
                          </div>
                        ) : (
                          <>
                            <div
                              style={{
                                width: DATE_COLUMN_WIDTH,
                                textAlign: "center",
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                lineHeight: "1.3em",
                              }}
                              onClick={() => setDate(task)}
                            >
                              <span style={{ fontWeight: 600 }}>
                                {formatDate(task.actual_start)}
                              </span>
                              <small style={{ color: "#555" }}>
                                ({formatDate(task.start)})
                              </small>
                            </div>
                            <div
                              style={{
                                width: DATE_COLUMN_WIDTH,
                                textAlign: "center",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                lineHeight: "1.3em",
                              }}
                              onClick={() => setDate(task)}
                            >
                              <span style={{ fontWeight: 600, color: "#000" }}>
                                {formatDate(task.actual_end)}
                              </span>
                              <small
                                style={{ color: "#777", fontStyle: "italic" }}
                              >
                                ({formatDate(task.end)})
                              </small>
                            </div>
                          </>
                        )}

                        {/* Budgeted Hrs */}
                        <div
                          style={{
                            width: HRS_COLUMN_WIDTH,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                          }}
                        >
                          {task.durations}
                        </div>

                        {/* Lock column for individual task */}
                        <div
                          style={{
                            width: LOCK_COLUMN_WIDTH,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i
                            className={`pi ${
                              task.is_locked ? "pi-lock" : "pi-lock-open"
                            }`}
                            style={{
                              color: task.is_locked ? "#6366F1" : "#22C55E",
                              fontSize: "1.4rem",
                              cursor: "pointer",
                              marginLeft: "-30px",
                            }}
                            title={
                              task.is_locked
                                ? "Task progress locked (click to edit & unlock)"
                                : "Task progress editable (click to edit & lock)"
                            }
                            onClick={() => setDate(task)}
                          ></i>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default CustomTaskListTable;